export type TicketStatus =
  | "todo"
  | "in_progress"
  | "in_review"
  | "done"
  | "hold"
  | "idea";

export type TicketType = "project" | "bug" | "feature" | "error";

export type TicketPriority = "low" | "medium" | "high" | "critical";

export type ViewMode = "table" | "board" | "calendar" | "weekly" | "archive";

export type CalendarViewMode = "month" | "week";

export type Subtask = {
  id: string;
  title: string;
  completed: boolean;
};

export type TicketComment = {
  id: string;
  author: string;
  text: string;
  createdAt: string;
};

export type Attachment = {
  id: string;
  name: string;
  size: number;
  type?: string;
  url: string;
};

export type Ticket = {
  id: string;
  key: string;
  title: string;
  description: string;
  status: TicketStatus;
  type: TicketType;
  assignee: string;
  labels: string[];
  dueDate: string | null;
  agentInput: string;
  targetVersion: string;
  url: string;
  shopifyPage: string;
  priority?: TicketPriority;
  isBacklog: boolean;
  isArchived: boolean;
  subtasks: Subtask[];
  comments: TicketComment[];
  attachments: Attachment[];
  creatorId: string;
  order: number;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TicketFilters = {
  statuses: TicketStatus[];
  types: TicketType[];
  priorities: TicketPriority[];
  assignee: string;
  reporters: string[];
  labels: string[];
  targetVersion: string;
  updatedFrom: string;
  updatedTo: string;
};

export const TICKET_STATUSES: { value: TicketStatus; label: string }[] = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "in_review", label: "In Review" },
  { value: "done", label: "Done" },
  { value: "hold", label: "Hold" },
];

export const STATUS_COLORS: Record<TicketStatus, string> = {
  todo: "grey.400",
  in_progress: "info.main",
  in_review: "warning.main",
  hold: "secondary.light",
  done: "success.main",
  idea: "info.light",
};

export const TICKET_TYPES: { value: TicketType; label: string }[] = [
  { value: "project", label: "Project" },
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
  { value: "error", label: "Error" },
];

export const TYPE_COLORS: Record<TicketType, string> = {
  project: "info.dark",
  bug: "error.main",
  feature: "success.dark",
  error: "error.main",
};

export const TICKET_PRIORITIES: { value: TicketPriority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

export const PRIORITY_COLORS: Record<TicketPriority, string> = {
  low: "grey.500",
  medium: "info.main",
  high: "warning.main",
  critical: "error.main",
};

export const NO_LABEL = "__no_label__";

export const REPO_LABELS = [
  "clock",
  "admin",
  "studio",
  "api",
  "editor",
  "mobile",
  "shopify",
];

export const LABEL_COLORS: Record<string, string> = {
  clock: "info.light",
  admin: "warning.light",
  studio: "success.light",
  api: "error.light",
  editor: "info.dark",
  mobile: "error.dark",
  shopify: "success.main",
};

export const SHOPIFY_PAGES = [
  "Home Page",
  "About Page",
  "Cart Page",
  "Klydoclock PDP",
  "Artists Page",
  "Checkout Page",
  "Collections PDP",
  "Press Page",
  "404",
  "Klydo Collections Page",
  "Reviews Page",
  "General",
  "Gift Page",
  "Contact Page",
  "Custom Page",
  "Support Page",
];

export const SHOPIFY_PAGE_COLORS: Record<string, string> = {
  "Home Page": "info.main",
  "About Page": "warning.main",
  "Cart Page": "error.main",
  "Klydoclock PDP": "success.main",
  "Artists Page": "secondary.main",
  "Checkout Page": "error.dark",
  "Collections PDP": "info.dark",
  "Press Page": "warning.dark",
  "404": "grey.600",
  "Klydo Collections Page": "success.dark",
  "Reviews Page": "info.light",
  // prettier-ignore
  "General": "grey.500",
  "Gift Page": "secondary.light",
  "Contact Page": "warning.light",
  "Custom Page": "success.light",
  "Support Page": "error.light",
};
